<template>
  <div class="modal-card">
    <header class="modal-card-head">
      <p class="modal-card-title">
        AI Enhanced Email
      </p>
      <button type="button" class="delete" @click="close" />
    </header>
    <section class="modal-card-body">
      <b-field label="Subject">
        <b-input
          v-model="emailData.subject"
          type="text"
          placeholder="Email subject"
        />
      </b-field>

      <b-field label="To">
        <b-input
          v-model="emailData.to"
          type="email"
          placeholder="Recipient email"
        />
      </b-field>

      <b-field label="Email Body">
        <textarea
          v-model="emailData.body"
          placeholder="Email body text"
          type="text"
          class="textarea"
        />
      </b-field>

      <b-loading v-model="isGenerating" :is-full-page="false" />
    </section>
    <footer class="modal-card-foot">
      <b-button
        label="AI Generate"
        type="is-info"
        :loading="isGenerating"
        @click="generateEmailBody"
      />
      <b-button
        label="Send"
        type="is-primary"
        :loading="isSending"
        @click="sendEmail"
      />
    </footer>
  </div>
</template>

<script>
import Assistance from '@/services/v2/assistance'
import { mapState } from 'vuex'
import Distribution from '../../services/v2/distribution'

export default {
  props: {
    distributor: {
      type: Object,
      required: true
    }
  },
  data () {
    return {
      emailData: {
        subject: '',
        to: '',
        body: ''
      },
      isGenerating: false,
      isSending: false
    }
  },
  computed: {
    ...mapState({
      user: state => state.auth.user
    })
  },
  mounted () {
    this.initializeEmail()
  },
  methods: {
    initializeEmail () {
      // Pre-fill destination email from emailConfig
      if (this.distributor.emailConfig?.setting?.sendTo) {
        this.emailData.to = this.distributor.emailConfig.setting.sendTo
      } else if (this.distributor.email) {
        this.emailData.to = this.distributor.email
      }

      // Pre-fill subject
      this.emailData.subject = `Reminder: ${this.distributor.name || this.distributor.id}`
    },
    async generateEmailBody () {
      this.isGenerating = true

      const country = this.distributor.country || 'English'

      const prompt = `You are a professional email writer for a distributor management system. Generate a polite reminder email in ${country} language for the following distributor account.

Distributor Information:
- Account ID: ${this.distributor.id}
- Name: ${this.distributor.name || ''}
- Country: ${this.distributor.country || ''}
- Email: ${this.distributor.email || ''}
- Status: ${this.distributor.emailConfig?.setting?.status || 'Inactive'}

Requirements:
1. Start with "Dear client," or appropriate greeting in ${country} language
2. Write the main content only (do NOT include subject line)
3. Politely inform them that they didn't upload required distribution documents yet
4. Encourage them to upload necessary documents
5. Keep it concise and professional
6. End with "Best regards," or appropriate greeting in ${country} language

Generate only the email body content, nothing else.`

      try {
        const result = await Assistance.chat({ message: prompt, history: [] })
        let replyText = result.reply || result.message || result.content || 'No response generated.'

        // Append signature with user information
        const signature = `\n\n${this.user.displayName || 'GPS Operations Team'}\nGPS Operations Team\nGlobal Packaging Services\nEmail: ${this.user.email || ''}`

        this.emailData.body = replyText + signature
      } catch (e) {
        this.$buefy.toast.open({
          message: e.message || 'Failed to generate email body.',
          type: 'is-danger',
          duration: 4000
        })
      } finally {
        this.isGenerating = false
      }
    },
    async sendEmail () {
      if (!this.emailData.to || !this.emailData.subject || !this.emailData.body) {
        this.$buefy.toast.open({
          message: 'Please fill in all fields.',
          type: 'is-warning',
          duration: 3000
        })
        return
      }

      this.isSending = true

      try {
        await Distribution.manualReminder({
          distributorId: this.distributor.id,
          sendTo: this.emailData.to,
          sendFrom: this.user.email,
          subject: this.emailData.subject,
          body: this.emailData.body
        })

        this.$buefy.toast.open({
          message: 'Email sent successfully!',
          type: 'is-primary',
          duration: 3000
        })
        this.close()
      } catch (e) {
        this.$buefy.toast.open({
          message: e.message || 'Failed to send email.',
          type: 'is-danger',
          duration: 4000
        })
      } finally {
        this.isSending = false
      }
    },
    close () {
      this.$emit('on-close')
    }
  }
}
</script>
